import { useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar, SnackbarStatus } from '@weareopenr/component-library';
import { createInstance, ICreateInstanceRequest, IExtensionInstance } from 'src/api/extensions';
import { useUserTracking } from '../useUserTracking/useUserTracking';
import { QueryKeys } from 'src/constants/QueryKeys';

interface ICreateInstanceParams {
  brandId: string;
  extensionId: string;
  payload: ICreateInstanceRequest;
}

export const useCreateInstance = (onSuccess?: (data: IExtensionInstance) => void) => {
  const { showSnackbar } = useSnackbar();
  const { captureException } = useUserTracking();
  const queryClient = useQueryClient();

  // Use ref to always have latest callback without recreating mutation
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  return useMutation({
    mutationFn: ({ brandId, extensionId, payload }: ICreateInstanceParams) =>
      createInstance(brandId, extensionId, payload),
    onSuccess: (data: IExtensionInstance, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.EXTENSIONS, variables.brandId] });
      const instanceName = data?.name || 'Instance';
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `${instanceName} has been created.`,
      });
      onSuccessRef.current?.(data);
    },
    onError: (error: Error) => {
      showSnackbar({
        message: error.message || 'Unable to create instance',
        status: SnackbarStatus.ERROR,
      });
      captureException(error);
    },
  });
};
